/**
 * @file The entry step: choose a distributor, stage parts by paste or by search, then Start.
 * Consumer: `SignifyFetchJobModalContent`. Both entry paths are always on screen; nothing toggles
 * between them, and paste sits on the left because it is the path a BOM arrives through.
 * It reads one leaf of the draft — the row count — because the search box is disabled at the cap;
 * everything else about the draft is read by the table, its cells and the actions row, each
 * subscribed to its own leaf, so a quantity edit re-renders one cell and nothing above it.
 * Nothing about another table's fetch reaches here: `uiState.signifyFetchJob` holds one job id per
 * `targetKey`, so a table with no key of its own is simply staging a list, and the only reasons
 * Start can carry are this table's own — no distributor, no parts, or no mapping configuration.
 */

import { FC } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { SIGNIFY_FETCH_JOB_MAX_INPUTS } from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import SignifyDistributorSelector from '../../SignifyProductModal/components/SignifyDistributorSelector';
import SignifyProductSearch from '../../SignifyProductModal/components/SignifyProductSearch';
import SignifyFetchJobEntryActions from './SignifyFetchJobEntryActions';
import SignifyFetchJobInputTable from './SignifyFetchJobInputTable';
import SignifyFetchJobPasteInput from './SignifyFetchJobPasteInput';
import { useSignifyFetchJobEntry } from '../use';
import type { SignifyFetchJobModalProps } from '../index.types';

const { Text } = Typography;

type SignifyFetchJobEntryProps = Pick<
  SignifyFetchJobModalProps,
  'targetKey' | 'hasQuantityColumn' | 'configDisabledReason'
>;

const SignifyFetchJobEntry: FC<SignifyFetchJobEntryProps> = ({
  targetKey,
  hasQuantityColumn,
  configDisabledReason,
}) => {
  const entry = useSignifyFetchJobEntry({ targetKey });
  const { keys } = useSnapshot(entry.draft);

  const atCap = keys.length >= SIGNIFY_FETCH_JOB_MAX_INPUTS;
  const searchDisabledReason = !entry.selectedDistributor
    ? 'Choose a distributor first.'
    : atCap
      ? `The list is full (${SIGNIFY_FETCH_JOB_MAX_INPUTS} parts).`
      : null;

  return (
    <FlexCol gap={10}>
      <SignifyDistributorSelector
        selectedDistributor={entry.selectedDistributor}
        distributorSearchValue={entry.distributorSearchValue}
        loading={entry.starting}
        loadingDistributors={entry.loadingDistributors}
        filteredDistributors={entry.filteredDistributors}
        filteredDistributorsCount={entry.filteredDistributorsCount}
        totalDistributors={entry.totalDistributors}
        distributorSearchTerm={entry.distributorSearchTerm}
        onDistributorSearch={entry.onDistributorSearch}
        onDistributorSelect={entry.onDistributorSelect}
        onDistributorClear={entry.onDistributorClear}
        onRefreshDistributors={entry.onRefreshDistributors}
      />

      <FlexBox alignStart gap={10}>
        <FlexCol gap={5} style={{ flexBasis: 0, minWidth: 0 }}>
          <Text>Paste codes</Text>
          <SignifyFetchJobPasteInput
            draft={entry.draft}
            distributorSelected={!!entry.selectedDistributor}
            onAdd={entry.addEntries}
          />
        </FlexCol>

        <FlexCol gap={5} style={{ flexBasis: 0, minWidth: 0 }}>
          <SignifyProductSearch
            productSearchValue={entry.productSearchValue}
            productOptions={entry.productOptions}
            searchingProducts={entry.searchingProducts}
            loading={!!searchDisabledReason}
            onProductSearch={entry.onProductSearch}
            onProductSelect={entry.onProductSelect}
            onProductClear={entry.onProductClear}
          />
          <Text type="secondary">
            {searchDisabledReason ?? 'One pick adds one part to the list below.'}
          </Text>
        </FlexCol>
      </FlexBox>

      <SignifyFetchJobInputTable
        draft={entry.draft}
        hasQuantityColumn={hasQuantityColumn}
        onCommitInput={entry.commitInputText}
        onRemove={entry.removeInput}
      />

      <SignifyFetchJobEntryActions
        draft={entry.draft}
        configDisabledReason={configDisabledReason}
        distributorSelected={!!entry.selectedDistributor}
        starting={entry.starting}
        onClear={entry.clearInputs}
        onStart={entry.onStart}
      />
    </FlexCol>
  );
};

export default SignifyFetchJobEntry;
